import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, TextField, Button,
  Stack, Box, MenuItem, IconButton, Typography, Divider, Snackbar, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { OrganizationHiretechupadate } from "../../../helper/base_url";

const EditHiringPostDialog = ({ open, onClose, post, onUpdate }) => {
  const [updates, setUpdates] = useState({});
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

  const opportunityTypes = ['Internship', 'Full-time Job', 'Freelance', 'Contract'];
  const locations = ['Remote', 'On-site', 'Hybrid'];
  const experiences = ['Fresher', '1-2 years', '2-5 years', '5+ years'];
  const domains = ['IoT', 'Embedded Systems', 'Data Science', 'AI/ML', 'Web/Mobile Development', 'Cybersecurity', 'DevOps', 'Other'];

  useEffect(() => {
    if (post) {
      setUpdates({
        opportunityOverview: {
          title: post.opportunityOverview?.title || '',
          opportunityType: post.opportunityOverview?.opportunityType || '',
          positions: post.opportunityOverview?.positions || '',
          location: post.opportunityOverview?.location || '',
          duration: post.opportunityOverview?.duration || '',
          experience: post.opportunityOverview?.experience || '',
        },
        roleDetails: {
          domain: post.roleDetails?.domain || '',
          roleOverview: post.roleDetails?.roleOverview || '',
          responsibilities: post.roleDetails?.responsibilities || '',
          deliverables: post.roleDetails?.deliverables || '',
          skills: post.roleDetails?.skills?.join(', ') || '',
          preferredBackground: post.roleDetails?.preferredBackground || '',
        },
        compensationAndDates: {
          stipendOrSalary: post.compensationAndDates?.stipendOrSalary || '',
          applicationDeadline: post.compensationAndDates?.applicationDeadline?.slice(0, 10) || '',
          joiningDate: post.compensationAndDates?.joiningDate?.slice(0, 10) || '',
        },
        contactInformation: {
          contactName: post.contactInformation?.contactName || '',
          contactEmail: post.contactInformation?.contactEmail || '',
          contactPhone: post.contactInformation?.contactPhone || '',
          linkedInProfile: post.contactInformation?.linkedInProfile || '',
        },
      });
    }
  }, [post]);

  const handleNestedChange = (section, field, value) => {
    setUpdates(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("company_token");
    try {
      await axios.put(
        `${OrganizationHiretechupadate}`,
        {
          hiringId: post._id,
          updates: {
            ...updates,
            opportunityOverview: {
              ...updates.opportunityOverview,
              positions: parseInt(updates.opportunityOverview.positions, 10)
            },
            roleDetails: {
              ...updates.roleDetails,
              skills: updates.roleDetails.skills.split(',').map(s => s.trim())
            },
            compensationAndDates: {
              ...updates.compensationAndDates,
              applicationDeadline: new Date(updates.compensationAndDates.applicationDeadline),
              joiningDate: updates.compensationAndDates.joiningDate
                ? new Date(updates.compensationAndDates.joiningDate)
                : null,
            }
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccessSnackbarOpen(true);
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Error updating post:", err);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Edit Hiring Post
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right : 8, top : 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={3}>
          <Section title="Opportunity Overview" />
          <TextField
            label="Title"
            placeholder="Enter job title"
            fullWidth
            value={updates.opportunityOverview?.title || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'title', e.target.value)}
          />
          <TextField
            select
            label="Opportunity Type"
            fullWidth
            placeholder="Select opportunity type"
            value={updates.opportunityOverview?.opportunityType || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'opportunityType', e.target.value)}
          >
            {opportunityTypes.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
          <TextField
            label="Number of Positions"
            placeholder="e.g., 5"
            type="number"
            fullWidth
            value={updates.opportunityOverview?.positions || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'positions', e.target.value)}
          />
          <TextField
            select
            label="Location"
            fullWidth
            value={updates.opportunityOverview?.location || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'location', e.target.value)}
          >
            {locations.map(loc => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
          </TextField>
          <TextField
            label="Duration"
            placeholder="e.g., 6 months"
            fullWidth
            value={updates.opportunityOverview?.duration || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'duration', e.target.value)}
          />
          <TextField
            select
            label="Experience"
            fullWidth
            value={updates.opportunityOverview?.experience || ''}
            onChange={(e) => handleNestedChange('opportunityOverview', 'experience', e.target.value)}
          >
            {experiences.map(exp => <MenuItem key={exp} value={exp}>{exp}</MenuItem>)}
          </TextField>

          <Section title="Role Details" />
          <TextField
            select
            label="Domain"
            fullWidth
            value={updates.roleDetails?.domain || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'domain', e.target.value)}
          >
            {domains.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <TextField
            label="Role Overview"
            placeholder="Describe the role"
            multiline
            fullWidth
            value={updates.roleDetails?.roleOverview || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'roleOverview', e.target.value)}
          />
          <TextField
            label="Responsibilities"
            placeholder="List the responsibilities"
            multiline
            fullWidth
            value={updates.roleDetails?.responsibilities || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'responsibilities', e.target.value)}
          />
          <TextField
            label="Deliverables"
            placeholder="Mention expected deliverables"
            multiline
            fullWidth
            value={updates.roleDetails?.deliverables || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'deliverables', e.target.value)}
          />
          <TextField
            label="Skills (comma-separated)"
            placeholder="e.g., React, Node.js, MongoDB"
            fullWidth
            value={updates.roleDetails?.skills || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'skills', e.target.value)}
          />
          <TextField
            label="Preferred Background"
            placeholder="e.g., B.Tech in CS, prior internship experience"
            fullWidth
            value={updates.roleDetails?.preferredBackground || ''}
            onChange={(e) => handleNestedChange('roleDetails', 'preferredBackground', e.target.value)}
          />

          <Section title="Compensation & Dates" />
          <TextField
            label="Stipend/Salary"
            placeholder="e.g., ₹15,000 per month"
            fullWidth
            value={updates.compensationAndDates?.stipendOrSalary || ''}
            onChange={(e) => handleNestedChange('compensationAndDates', 'stipendOrSalary', e.target.value)}
          />
          <TextField
            label="Application Deadline"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={updates.compensationAndDates?.applicationDeadline || ''}
            onChange={(e) => handleNestedChange('compensationAndDates', 'applicationDeadline', e.target.value)}
          />
          <TextField
            label="Joining Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={updates.compensationAndDates?.joiningDate || ''}
            onChange={(e) => handleNestedChange('compensationAndDates', 'joiningDate', e.target.value)}
          />

          <Section title="Contact Information" />
          <TextField
            label="Contact Name"
            placeholder="Full name of contact person"
            fullWidth
            value={updates.contactInformation?.contactName || ''}
            onChange={(e) => handleNestedChange('contactInformation', 'contactName', e.target.value)}
          />
          <TextField
            label="Contact Email"
            placeholder="e.g., contact@example.com"
            fullWidth
            value={updates.contactInformation?.contactEmail || ''}
            onChange={(e) => handleNestedChange('contactInformation', 'contactEmail', e.target.value)}
          />
          <TextField
            label="Contact Phone"
            placeholder="e.g., +91 9876543210"
            fullWidth
            value={updates.contactInformation?.contactPhone || ''}
            onChange={(e) => handleNestedChange('contactInformation', 'contactPhone', e.target.value)}
          />
          <TextField
            label="LinkedIn Profile"
            placeholder="https://linkedin.com/in/username"
            fullWidth
            value={updates.contactInformation?.linkedInProfile || ''}
            onChange={(e) => handleNestedChange('contactInformation', 'linkedInProfile', e.target.value)}
          />
        </Stack>

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Save Changes
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
     <Snackbar
      open={successSnackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSuccessSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessSnackbarOpen(false)} severity="success" sx={{ width : '100%' }}>
        Hiring post updated successfully!
      </Alert>
    </Snackbar>
    </>
  );
};

const Section = ({ title }) => (
  <Box>
    <Divider sx={{ my: 2 }} />
    <Typography variant="h6" color="#043885">
      {title}
    </Typography>
  </Box>
);

export default EditHiringPostDialog;
